import axios, { CancelTokenSource } from 'axios'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useErrorHandler } from 'react-error-boundary'

import { AppContext } from '../contexts/app-context'
import API from './api'
import { parseNotificationPreferences } from './helpers'

const useFolderContacts = ({ folderId }: { folderId: string | number }) => {
  const _isMounted = useRef(true)
  const handleError = useErrorHandler()
  const { folders, foldersLoading } = useContext(AppContext)
  const folder = useMemo(() => folders.find(f => f.id === folderId), [folders, folderId])

  // This allow us to cancel an axios call in the useEffect cleanup function when unmounting the component
  const [source] = useState<CancelTokenSource>(axios.CancelToken.source())
  const [loading, setLoading] = useState<boolean>(true)
  const [contacts, setContacts] = useState<INotificationContactsType>()

  const receiveAlertsCount = useMemo(
    () => contacts?.users.filter(c => c.user.preferences.notification?.length > 0).length || 0,
    [contacts?.users]
  )
  const userCount = useMemo(() => contacts?.users.length || 0, [contacts?.users])

  const fetchUsers = useCallback(async () => {
    try {
      const notificationContactsResponse = (
        await API.get(`/api/v2/protect/folders/${folderId}/notification-contacts`, {
          cancelToken: source.token,
        })
      ).data as INotificationContactsAPIType

      const data: INotificationContactsType = {
        ...notificationContactsResponse,
        users: notificationContactsResponse.users.map(u => {
          const contact: INotificationContactType = {
            ...u,
            doNotContact: !!(
              parseNotificationPreferences(u.user.preferences.notification).length === 0 && u.user.phone
            ),
          }
          return contact
        }),
      }
      setContacts(data)
    } catch (e) {
      handleError(e as unknown as Error)
    } finally {
      setLoading(false)
    }
  }, [folderId, handleError, source])

  useEffect(() => {
    if (folderId && folder?.folderType === 'Participant') fetchUsers()
    if (folderId && !foldersLoading && folder?.folderType !== 'Participant') setLoading(false)
  }, [fetchUsers, folder, folder?.folderType, folderId, foldersLoading])

  // Clean up when the component unmounts
  useEffect(() => {
    return () => {
      source.cancel('cancelled') // clean up axios calls when component unmounts
      _isMounted.current = false // used to handle memory leaks when performing a state change when the component has already unmounted
    }
  }, [source])

  return { contacts, receiveAlertsCount, userCount, loading, fetchUsers }
}

export default useFolderContacts
